import {
  ChangeEventHandler,
  CSSProperties,
  FocusEventHandler,
  ForwardedRef,
  forwardRef,
  useMemo,
  KeyboardEventHandler,
  useState,
} from "react";
import styles from "./Input.module.css"
import { Eye } from "../Icons/Eye";
import { EyeSlash } from "../Icons/EyeSlash";

export interface IInputProps {
  multiline?: boolean;
  id?: string;
  type?: "text" | "number" | "password" | "date" | "file";
  name?: string;
  value?: string | number;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  style?: CSSProperties;
  min?: string | number;
  max?: string | number;
  maxLength?: number;
  disableArrows?: boolean;
  accept?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  autofocus?: boolean;
  hidden?: boolean;
  step?: string;
  className?: string;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}

const Input = forwardRef(function Input(
  props: IInputProps,
  ref: ForwardedRef<HTMLTextAreaElement & HTMLInputElement>,
) {
  const InputBase = (props.multiline ? "textarea" : "input") as "input";

  const [showPassword, setShowPassword] = useState(false);

  const _className = useMemo(() => (
    [
      styles.root,
      props.className,
      props.error && styles.error,
      props.disableArrows && styles.disableArrows
    ].filter(x => x !== undefined).join(" ")
  ), [props.className, props.error, props.disableArrows]);

  const type = useMemo(() => (
    props.type === "password" && !showPassword ? "password" : props.type === "number" ? "number" : "text"
  ), [props.type, showPassword]);

  const inputValue = useMemo(() => (
    props.type === "number" && props.maxLength ? props.value?.toString().slice(0, props.maxLength) : props.value
  ), [props.type, props.value]);

  const handleKeyDown = (evt: any) => {
    if (props.type === "number") {
      if (
        evt.key === 'e' ||
        evt.key === 'E' ||
        evt.key === '+' ||
        evt.key === '-' ||
        (!/[0-9]/.test(evt.key) &&
          evt.key !== 'Backspace' &&
          evt.key !== 'Tab' &&
          evt.key !== 'Delete' &&
          evt.key !== 'ArrowLeft' &&
          evt.key !== 'ArrowRight' &&
          evt.key !== 'Enter')
      ) {
        evt.preventDefault();
      }
    }
  };

  return (
    <>
      <InputBase
        ref={ref}
        id={props.id}
        type={type}
        name={props.name}
        className={_className}
        value={inputValue}
        disabled={props.disabled}
        placeholder={props.placeholder}
        onChange={props.onChange}
        onBlur={props.onBlur}
        style={props.style}
        maxLength={props.maxLength}
        accept={props.accept}
        min={props.min}
        max={props.max}
        autoFocus={props.autofocus}
        hidden={props.hidden}
        step={props.step}
        onKeyDown={handleKeyDown}
      />
      {
        (props.type === "password" && !showPassword) ?
          <div className={styles.icon} onClick={() => setShowPassword(true)}>
            <Eye />
          </div>
          :
          (type === "text" && showPassword && props.type === "password") &&
          <div className={styles.icon} onClick={() => setShowPassword(false)}>
            <EyeSlash />
          </div>
      }
    </>
  );
});

export default Input;
